// assets/AllCourses/Exercises/CircleTheCorrectWordExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function CircleTheCorrectWordExercise({
  exercise = {},
  onUpdate,           // present in edit mode
  errors = {},
  isPreview = false,  // false = admin edit, true = student
  showResult = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ---------------- SAFE QUESTIONS ---------------- */
  const questions = Array.isArray(exercise.wordChoiceQuestions)
    ? exercise.wordChoiceQuestions
    : [];

  /* ---------------- STUDENT STATE ---------------- */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ---------------- EDIT MODE HELPERS ---------------- */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordChoiceQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      correct: "",
      requireCapitalization: false,
    };

    next[qIdx] = {
      ...current,
      [field]: value,
    };

    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };

    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;

    updateQuestions([
      ...questions,
      {
        sentence: "",
        correct: "",
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ---------------- STUDENT CLICK ---------------- */
  const handleWordClick = (qIndex, word) => {
    if (isEditMode || showResult) return;

    const nextAnswers = { ...answers, [qIndex]: word };
    setAnswers(nextAnswers);

    const allAnswered = questions.every(
      (_, i) => nextAnswers[i] !== undefined
    );

    if (allAnswered) {
      checkAnswers(nextAnswers);
    }
  };

  const checkAnswers = (finalAnswers) => {
    if (completedRef.current) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, idx) => {
      const user = String(finalAnswers[idx] || "").trim();
      const correct = String(q.correct || "").trim();

      return q.requireCapitalization
        ? user === correct
        : user.toLowerCase() === correct.toLowerCase();
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ---------------- SENTENCE RENDERER ---------------- */
  const renderSentence = (q, qIndex) => {
    if (!q?.sentence) {
      return (
        <span className="text-muted-foreground">
          No sentence provided.
        </span>
      );
    }

    const parts = q.sentence.split(/[\(\)]/);

    return parts.map((part, idx) => {
      // options block
      if (idx % 2 === 1) {
        const options = part
          .split("/")
          .map((o) => o.trim())
          .filter(Boolean);

        return (
          <span key={idx} className="inline mx-1">
            {options.map((word) => {
              const selected = answers[qIndex] === word;
              const isCorrect =
                word.trim().toLowerCase() ===
                String(q.correct || "").trim().toLowerCase();

              return (
                <button
                  key={word}
                  type="button"
                  onClick={() => handleWordClick(qIndex, word)}
                  disabled={isEditMode || showResult}
                  className={cn(
                    "px-3 py-1 mx-1 rounded border transition-all",
                    selected
                      ? "bg-primary text-primary-foreground"
                      : "bg-background hover:bg-accent",
                    showResult &&
                      selected &&
                      !isCorrect &&
                      "bg-destructive text-destructive-foreground",
                    showResult &&
                      !selected &&
                      isCorrect &&
                      "border-green-500 bg-green-50"
                  )}
                >
                  {word}
                  {showResult && selected && (
                    <span className="ml-2 text-xs">
                      {isCorrect
                        ? "Correct"
                        : `Wrong → ${q.correct}`}
                    </span>
                  )}
                </button>
              );
            })}
          </span>
        );
      }

      return <span key={idx}>{part}</span>;
    });
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-6">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-4">
            <div className="space-y-4">
              <TextareaField
                label={`Sentence ${qIdx + 1}`}
                value={q.sentence || ""}
                onChange={(v) =>
                  handleQuestionChange(qIdx, "sentence", v)
                }
                placeholder="The (big/small) house is beautiful."
                rows={3}
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-wc-sentence-${qIdx}`
                  ]
                }
              />

              <StringField
                label="Correct Answer"
                value={q.correct || ""}
                onChange={(v) =>
                  handleQuestionChange(qIdx, "correct", v)
                }
                placeholder="big"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-wc-correct-${qIdx}`
                  ]
                }
              />

              <p className="text-xs text-muted-foreground">
                Use brackets <code>( )</code> and slashes <code>/</code> to define options.
                <br />
                Example: <code>The (big/small) house is beautiful.</code>
              </p>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  handleCapitalizationChange(qIdx, c)
                }
              />

              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleRemoveQuestion(qIdx)}
              >
                Remove Question
              </Button>
            </div>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardDescription>
          Click the correct word in each sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {questions.map((q, qIdx) => (
          <div
            key={qIdx}
            className="p-5 bg-muted rounded-lg text-lg leading-relaxed"
          >
            {renderSentence(q, qIdx)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
